import type { LargeMenuOptimizations } from '@/app/[domain]/[lang]/office-catering/vendors/[permalink]/page'
import { pluralize } from '@/helpers/strings'
import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Box,
  Divider,
  useTheme,
  Skeleton,
  CircularProgress,
} from '@mui/material'

import React, { useState, Suspense, memo, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { MenuCategory } from '../../domain/menu-category'
import { isMenuItemIndex } from '../../domain/menu-item-index'
import type { MenuVendor } from '../../domain/menu-vendor'
import { useLazyCategoryItems } from '../../hooks/useLazyCategoryItems'
import { useMenuPageState } from '../../states/menu-page'
import {
  useCategoryItemsStore,
  useCategoryLoadState,
  useCategoryFullItems,
} from '../../store/categoryItemsStore'
import { useHasActiveFilters } from '../../store/gmFiltersStore'
import { MenuItemCardProvider } from '../menu-item-card'
import { MenuItemsRenderer } from '../menu-items-renderer/menu-items-renderer'
import FilterLoadingOverlay from './FilterLoadingOverlay'

type Props = {
  menuItemsState: ReturnType<typeof useMenuPageState>
  vendor: MenuVendor
  taxAcronym: string
  largeMenuOptimizations?: LargeMenuOptimizations
}

type CategoryAccordionItemProps = {
  category: MenuCategory
  vendor: MenuVendor
  taxAcronym: string
  loadMore?: (() => void) | null
  loadingMore?: boolean | null
  index: number
  largeMenuOptimizations?: LargeMenuOptimizations
  hasActiveFilters?: boolean
}

const CategoryAccordionSkeleton: React.FC = () => (
  <Box
    sx={{
      backgroundColor: 'background.paper',
      borderRadius: 2,
      mb: 1,
      p: 2,
    }}>
    <Skeleton variant="text" width={180} height={32} />
  </Box>
)

/**
 * Skeleton component for category items loading state
 */
const CategoryItemsSkeleton: React.FC = () => (
  <Box sx={{ px: 2, py: 3 }}>
    <Stack spacing={2}>
      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
    </Stack>
  </Box>
)

const CategoryAccordionItem: React.FC<CategoryAccordionItemProps> = ({
  category,
  vendor,
  taxAcronym,
  loadMore,
  loadingMore,
  index,
  largeMenuOptimizations,
  hasActiveFilters,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  // Check if this category has index-only items that need full details loaded
  const hasIndexOnlyItems = useMemo(() => {
    return category.items.some((item) => isMenuItemIndex(item))
  }, [category.items])

  // Determine if this category should use hybrid loading
  const useHybridLoading =
    largeMenuOptimizations?.enabled && largeMenuOptimizations.useHybridLoading
  const preloadCategoryCount = largeMenuOptimizations?.preloadCategoryCount ?? 2
  const needsLazyLoad = useHybridLoading && hasIndexOnlyItems && index >= preloadCategoryCount

  // Use the lazy loading hook
  const { loadState, loading, loadFullItems, fullItems } = useLazyCategoryItems({
    vendorId: vendor.id,
    categoryId: category.id,
  })

  // Initialize the store with index-only state if needed
  const { markCategoryAsIndexOnly } = useCategoryItemsStore(
    useShallow((state) => ({
      markCategoryAsIndexOnly: state.markCategoryAsIndexOnly,
    })),
  )

  // Mark category as having index-only items on mount
  useEffect(() => {
    if (needsLazyLoad && loadState !== 'loaded') {
      markCategoryAsIndexOnly(category.id)
    }
  }, [needsLazyLoad, category.id, markCategoryAsIndexOnly, loadState])

  const shouldStartExpanded = largeMenuOptimizations?.enabled
    ? index < (largeMenuOptimizations.initialExpandedCategories ?? 2)
    : true

  // Determine if this accordion should be expanded
  // When filters are active and category has items, always expand
  // Otherwise, use the default expansion logic
  const shouldBeExpanded = hasActiveFilters ? category.items.length > 0 : shouldStartExpanded

  const [isExpanded, setIsExpanded] = useState(shouldStartExpanded)
  const [hasBeenExpanded, setHasBeenExpanded] = useState(shouldStartExpanded)
  const prevShouldBeExpandedRef = useRef(shouldBeExpanded)

  // Only update expansion state when shouldBeExpanded actually changes
  useEffect(() => {
    if (shouldBeExpanded !== prevShouldBeExpandedRef.current) {
      setIsExpanded(shouldBeExpanded)
      if (shouldBeExpanded) {
        setHasBeenExpanded(true)
      }
      prevShouldBeExpandedRef.current = shouldBeExpanded
    }
  }, [shouldBeExpanded])

  // Trigger lazy load when accordion is expanded and has index-only items
  useEffect(() => {
    if (isExpanded && needsLazyLoad && loadState === 'index') {
      void loadFullItems()
    }
  }, [isExpanded, needsLazyLoad, loadState, loadFullItems])

  const handleExpansionChange = useCallback(
    (_event: React.SyntheticEvent, expanded: boolean) => {
      setIsExpanded(expanded)
      if (expanded && !hasBeenExpanded) {
        setHasBeenExpanded(true)
      }

      // Trigger lazy load when expanding if we have index-only items
      if (expanded && needsLazyLoad && loadState === 'index') {
        void loadFullItems()
      }
    },
    [hasBeenExpanded, needsLazyLoad, loadState, loadFullItems],
  )

  const itemCount = category.items.length
  const itemCountLabel = t('marketplace:itemsCount', {
    count: itemCount,
    defaultValue: `${itemCount} ${pluralize('item', itemCount)}`,
  })
  const hasMore = false

  const displayItems = useMemo(() => {
    if (needsLazyLoad && fullItems && loadState === 'loaded') {
      return fullItems
    }
    return category.items
  }, [needsLazyLoad, fullItems, loadState, category.items])

  const shouldUseVirtualization = largeMenuOptimizations?.enabled
    ? largeMenuOptimizations.useVirtualization && displayItems.length > 20
    : false

  const shouldLazyLoad = largeMenuOptimizations?.enabled
    ? (largeMenuOptimizations.lazyLoadAccordions ?? true)
    : false

  const shouldRenderContent = shouldLazyLoad ? hasBeenExpanded : true

  const isLoadingFullItems = needsLazyLoad && loading

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleExpansionChange}
      elevation={0}
      sx={{
        backgroundColor: 'transparent',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          backgroundColor: 'background.paper',
          border: theme.borders.light,
          transition: 'box-shadow 0.2s ease-in-out',
          borderRadius: 2,
        },
        '& .MuiAccordionSummary-content': {
          marginY: 0,
        },
        mb: 1,
      }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {category.name}
          </Typography>
          <Divider orientation="vertical" sx={{ mx: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {itemCountLabel}
          </Typography>
          {isLoadingFullItems && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, pt: 1 }}>
        {!shouldRenderContent ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Skeleton variant="rectangular" height={120} />
          </Box>
        ) : isLoadingFullItems ? (
          <CategoryItemsSkeleton />
        ) : displayItems.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('marketplace:oopsLooksLikeNo')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('marketplace:removeTheFiltersTo')}
            </Typography>
          </Box>
        ) : (
          <MenuItemsRenderer
            menuItems={displayItems}
            vendor={vendor}
            taxAcronym={taxAcronym}
            loadingMore={loadingMore ?? false}
            useVirtualisedList={shouldUseVirtualization}
            onLoadMore={undefined}
            hasMore={hasMore}
            useOptimizedCard
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

const MemoizedCategoryAccordionItem = memo(CategoryAccordionItem, (prevProps, nextProps) => {
  return (
    prevProps.category.id === nextProps.category.id &&
    prevProps.category.items.length === nextProps.category.items.length &&
    prevProps.vendor.id === nextProps.vendor.id &&
    prevProps.index === nextProps.index &&
    prevProps.loadingMore === nextProps.loadingMore &&
    prevProps.hasActiveFilters === nextProps.hasActiveFilters
  )
})

MemoizedCategoryAccordionItem.displayName = 'MemoizedCategoryAccordionItem'

const CategoryAccordion: React.FC<Props> = ({
  menuItemsState,
  vendor,
  taxAcronym,
  largeMenuOptimizations,
}) => {
  const { t } = useTranslation()

  // Use optimized selector - only re-renders when the boolean value changes
  const hasActiveFilters = useHasActiveFilters()

  const itemsStateLoaded = menuItemsState.type === 'loaded'

  const loadMore = itemsStateLoaded ? menuItemsState.loadMore : null
  const loadingMore = itemsStateLoaded ? menuItemsState.loadingMore : null
  const availability = itemsStateLoaded ? menuItemsState.availability : null
  const menuCategories = availability ? availability.menuCategories : []

  if (!menuCategories.length && menuItemsState.type === 'loading') {
    return (
      <Box sx={{ mt: 2 }}>
        <Skeleton height={120} sx={{ mb: 1 }} />
        <Skeleton height={120} />
      </Box>
    )
  }

  if (!menuCategories.length && menuItemsState.type === 'loaded') {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant="body2" color="text.secondary" textAlign={'center'}>
          {t('marketplace:oopsLooksLikeNo')}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign={'center'}>
          {t('marketplace:removeTheFiltersTo')}
        </Typography>
      </Box>
    )
  }

  return (
    <MenuItemCardProvider vendor={vendor}>
      <FilterLoadingOverlay loading={false}>
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1}>
            {menuCategories.map((menuCategory, index) => (
              <Suspense key={menuCategory.id} fallback={<CategoryAccordionSkeleton />}>
                <MemoizedCategoryAccordionItem
                  category={menuCategory}
                  vendor={vendor}
                  taxAcronym={taxAcronym}
                  loadMore={loadMore}
                  loadingMore={loadingMore}
                  index={index}
                  largeMenuOptimizations={largeMenuOptimizations}
                  hasActiveFilters={hasActiveFilters}
                />
              </Suspense>
            ))}
          </Stack>
        </Box>
      </FilterLoadingOverlay>
    </MenuItemCardProvider>
  )
}

export default CategoryAccordion
